import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { PageFlip } from 'page-flip';

const styles = require('./librarian_reader.styl');
const cx = classNames.bind(styles);

const BASE_PATH = typeof LIBRARIAN_BASE_PATH !== 'undefined' ? LIBRARIAN_BASE_PATH : '/librarian';

const DEFAULT_SCALE = 0.8;
const MIN_SCALE = 0.5;
const MAX_SCALE = 1;
const DEFAULT_PAGE_RENDER_SCALE = 1.35;
const DEFAULT_ZOOM_SCALE = 1;
const FLIPBOOK_WIDTH = 460;
const FLIPBOOK_HEIGHT = 640;
const FLIPBOOK_MIN_HEIGHT = 420;
const FLIPBOOK_MAX_HEIGHT = 1600;
const DEFAULT_PAGE_ASPECT_RATIO = FLIPBOOK_WIDTH / FLIPBOOK_HEIGHT;
const MIN_PAGE_ASPECT_RATIO = 0.45;
const MAX_PAGE_ASPECT_RATIO = 2.2;
const BACKGROUND_UPDATE_BATCH_SIZE = 4;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const createPlaceholderPage = () => {
  const placeholder = document.createElement('canvas');
  placeholder.width = 20;
  placeholder.height = 20;
  const context = placeholder.getContext('2d');
  context.fillStyle = '#f5f5f5';
  context.fillRect(0, 0, placeholder.width, placeholder.height);
  return placeholder.toDataURL('image/png');
};

const addTrailingBlank = (pages, placeholderPage = createPlaceholderPage()) => {
  if (pages.length % 2 === 0) {
    return pages;
  }
  return pages.concat([placeholderPage]);
};

const getFlipbookOptions = (pageAspectRatio = DEFAULT_PAGE_ASPECT_RATIO) => ({
  width: Math.round(FLIPBOOK_HEIGHT * pageAspectRatio),
  height: FLIPBOOK_HEIGHT,
  maxShadowOpacity: 0.25,
  showCover: false,
  useMouseEvents: true,
  minWidth: Math.round(FLIPBOOK_MIN_HEIGHT * pageAspectRatio),
  minHeight: FLIPBOOK_MIN_HEIGHT,
  maxWidth: Math.round(FLIPBOOK_MAX_HEIGHT * pageAspectRatio),
  maxHeight: FLIPBOOK_MAX_HEIGHT,
  size: 'stretch',
  usePortrait: false,
  mobileScrollSupport: false,
});

const preserveAndRestorePageIndex = (flipbook, targetPageIndex) => {
  if (!flipbook || typeof targetPageIndex !== 'number' || targetPageIndex < 0) {
    return;
  }
  flipbook.turnToPage(targetPageIndex);
};

const getDevicePixelRatio = () => {
  if (typeof window === 'undefined') {
    return 1;
  }
  return window.devicePixelRatio || 1;
};

const LibrarianReader = ({ assetId }) => {
  const viewerRef = useRef(null);
  const containerRef = useRef(null);
  const flipInstance = useRef(null);
  const flipHostRef = useRef(null);
  const previousAssetIdRef = useRef(assetId);
  const previousAppliedZoomPercentRef = useRef(DEFAULT_ZOOM_SCALE * 100);
  const forceCenterOnNextBoundsUpdateRef = useRef(true);
  const totalDocumentPagesRef = useRef(0);
  const pageAspectRatioRef = useRef(DEFAULT_PAGE_ASPECT_RATIO);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageInfo, setPageInfo] = useState({ current: 0, total: 0 });
  const [hasPages, setHasPages] = useState(false);
  const [scale] = useState(DEFAULT_SCALE);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [appliedZoomPercent, setAppliedZoomPercent] = useState(100);
  const [isZoomDebouncing, setIsZoomDebouncing] = useState(false);
  const [isBackgroundRendering, setIsBackgroundRendering] = useState(false);
  const [pdfLib, setPdfLib] = useState(() => (typeof window !== 'undefined' ? window.pdfjsLib : null));

  const updateFlipbookBounds = useCallback(() => {
    if (typeof window === 'undefined' || !containerRef.current) {
      return;
    }
    const viewerNode = viewerRef.current || containerRef.current.parentElement;
    if (!viewerNode) {
      return;
    }
    const previousWidth = containerRef.current.offsetWidth;
    const previousHeight = containerRef.current.offsetHeight;
    const previousScrollLeft = viewerNode.scrollLeft;
    const previousScrollTop = viewerNode.scrollTop;
    const hadScrollableWidth = previousWidth > viewerNode.clientWidth;
    const hadScrollableHeight = previousHeight > viewerNode.clientHeight;
    const shouldForceCenter = forceCenterOnNextBoundsUpdateRef.current;
    const previousCenterRatioX = previousWidth > 0
      ? clamp(
        hadScrollableWidth
          ? (previousScrollLeft + (viewerNode.clientWidth / 2)) / previousWidth
          : 0.5,
        0,
        1,
      )
      : null;
    const previousCenterRatioY = previousHeight > 0
      ? clamp(
        hadScrollableHeight
          ? (previousScrollTop + (viewerNode.clientHeight / 2)) / previousHeight
          : 0.5,
        0,
        1,
      )
      : null;
    const targetCenterRatioX = shouldForceCenter ? 0.5 : previousCenterRatioX;
    const targetCenterRatioY = shouldForceCenter ? 0 : previousCenterRatioY;

    const normalizedScale = clamp(scale, MIN_SCALE, MAX_SCALE);
    const displayZoomScale = appliedZoomPercent / 100;
    const twoPageBookAspectRatio = pageAspectRatioRef.current * 2;
    const availableWidth = Math.max(1, Math.floor(viewerNode.clientWidth * normalizedScale));
    const availableHeight = Math.max(1, Math.floor(viewerNode.clientHeight * normalizedScale));
    const fittedBaseWidth = Math.min(
      availableWidth,
      Math.floor(availableHeight * twoPageBookAspectRatio),
    );
    const fittedBaseHeight = Math.max(1, Math.floor(fittedBaseWidth / twoPageBookAspectRatio));

    const width = Math.floor(fittedBaseWidth * displayZoomScale);
    const height = Math.floor(fittedBaseHeight * displayZoomScale);

    containerRef.current.style.width = `${Math.max(1, width)}px`;
    containerRef.current.style.height = `${Math.max(1, height)}px`;
    containerRef.current.style.maxWidth = 'none';
    containerRef.current.style.maxHeight = 'none';

    if (targetCenterRatioX === null || targetCenterRatioY === null) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!viewerRef.current || !containerRef.current) {
        return;
      }
      const nextWidth = containerRef.current.offsetWidth;
      const nextHeight = containerRef.current.offsetHeight;
      const maxScrollLeft = Math.max(0, nextWidth - viewerRef.current.clientWidth);
      const maxScrollTop = Math.max(0, nextHeight - viewerRef.current.clientHeight);
      if (shouldForceCenter) {
        viewerRef.current.scrollLeft = Math.round(maxScrollLeft / 2);
        viewerRef.current.scrollTop = 0;
        forceCenterOnNextBoundsUpdateRef.current = false;
        return;
      }

      const nextCenterX = targetCenterRatioX * nextWidth;
      const nextCenterY = targetCenterRatioY * nextHeight;
      viewerRef.current.scrollLeft = clamp(
        Math.round(nextCenterX - (viewerRef.current.clientWidth / 2)),
        0,
        maxScrollLeft,
      );
      viewerRef.current.scrollTop = clamp(
        Math.round(nextCenterY - (viewerRef.current.clientHeight / 2)),
        0,
        maxScrollTop,
      );
    });
  }, [appliedZoomPercent, scale]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleReady = () => {
      if (window.pdfjsLib) {
        setPdfLib(window.pdfjsLib);
      }
    };
    window.addEventListener('pdfjslibready', handleReady);
    handleReady();
    return () => {
      window.removeEventListener('pdfjslibready', handleReady);
    };
  }, []);

  const cleanupFlipbook = useCallback(() => {
    if (flipInstance.current) {
      try {
        flipInstance.current.destroy();
      } catch (error) {
        console.warn('Failed to fully destroy flipbook instance', error);
      }
      flipInstance.current = null;
    }
    if (flipHostRef.current && flipHostRef.current.parentNode) {
      flipHostRef.current.parentNode.removeChild(flipHostRef.current);
    }
    flipHostRef.current = null;
  }, []);

  const ensureFlipHost = useCallback(() => {
    if (!containerRef.current) {
      return null;
    }

    if (!flipHostRef.current || !containerRef.current.contains(flipHostRef.current)) {
      const host = document.createElement('div');
      host.style.width = '100%';
      host.style.height = '100%';
      host.style.position = 'relative';
      containerRef.current.appendChild(host);
      flipHostRef.current = host;
    }

    return flipHostRef.current;
  }, []);

  const renderPageToImage = useCallback(async (
    documentInstance,
    pageIndex,
    { zoomScale = DEFAULT_ZOOM_SCALE, dpr = 1 } = {},
  ) => {
    const outputScale = Math.max(1, dpr);
    const page = await documentInstance.getPage(pageIndex + 1);
    const viewport = page.getViewport({ scale: DEFAULT_PAGE_RENDER_SCALE * zoomScale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);

    const renderContext = {
      canvasContext: context,
      viewport,
    };

    if (outputScale !== 1) {
      renderContext.transform = [outputScale, 0, 0, outputScale, 0, 0];
    }

    await page.render(renderContext).promise;
    return {
      dataUrl: canvas.toDataURL('image/png'),
      aspectRatio: viewport.width / Math.max(1, viewport.height),
    };
  }, []);

  const updatePageAspectRatioFromRender = useCallback((nextAspectRatio) => {
    if (!Number.isFinite(nextAspectRatio) || nextAspectRatio <= 0) {
      return;
    }
    const clampedRatio = clamp(nextAspectRatio, MIN_PAGE_ASPECT_RATIO, MAX_PAGE_ASPECT_RATIO);
    if (Math.abs(clampedRatio - pageAspectRatioRef.current) < 0.001) {
      return;
    }
    pageAspectRatioRef.current = clampedRatio;
  }, []);

  const initFlipbook = useCallback((
    pages,
    { restoreFromPageIndex = null, forceRecreate = false } = {},
  ) => {
    if (!pages.length) {
      setHasPages(false);
      return;
    }

    const canUpdateInPlace = Boolean(flipInstance.current) && !forceRecreate;

    if (canUpdateInPlace) {
      flipInstance.current.updateFromImages(pages);
      if (typeof restoreFromPageIndex === 'number' && restoreFromPageIndex >= 0) {
        preserveAndRestorePageIndex(flipInstance.current, restoreFromPageIndex);
      } else {
        preserveAndRestorePageIndex(flipInstance.current, 0);
      }
      setHasPages(true);
      return;
    }

    cleanupFlipbook();
    const host = ensureFlipHost();
    if (!host) {
      setHasPages(false);
      return;
    }

    const pageFlip = new PageFlip(host, getFlipbookOptions(pageAspectRatioRef.current));
    pageFlip.on('flip', (event) => {
      const totalDocumentPages = totalDocumentPagesRef.current || pages.length;
      setPageInfo({
        current: Math.min(event.data + 1, totalDocumentPages),
        total: totalDocumentPages,
      });
    });
    pageFlip.loadFromImages(pages);
    preserveAndRestorePageIndex(pageFlip, restoreFromPageIndex);

    flipInstance.current = pageFlip;
    setHasPages(true);
    updateFlipbookBounds();
  }, [cleanupFlipbook, ensureFlipHost, updateFlipbookBounds]);

  useEffect(() => {
    if (zoomPercent === appliedZoomPercent) {
      setIsZoomDebouncing(false);
      return undefined;
    }

    setIsZoomDebouncing(true);
    const timeoutId = setTimeout(() => {
      setAppliedZoomPercent(zoomPercent);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [appliedZoomPercent, zoomPercent]);

  useEffect(() => {
    let isCancelled = false;
    const previousAssetId = previousAssetIdRef.current;
    const previousAppliedZoomPercent = previousAppliedZoomPercentRef.current;
    const assetChanged = previousAssetId !== assetId;
    const zoomChanged = previousAppliedZoomPercent !== appliedZoomPercent;
    previousAssetIdRef.current = assetId;
    previousAppliedZoomPercentRef.current = appliedZoomPercent;
    if (assetChanged || zoomChanged) {
      forceCenterOnNextBoundsUpdateRef.current = true;
    }

    if (!assetId) {
      cleanupFlipbook();
      totalDocumentPagesRef.current = 0;
      pageAspectRatioRef.current = DEFAULT_PAGE_ASPECT_RATIO;
      forceCenterOnNextBoundsUpdateRef.current = true;
      setStatus(null);
      setPageInfo({ current: 0, total: 0 });
      setHasPages(false);
      setIsBackgroundRendering(false);
      setBusy(false);
      return () => {
        isCancelled = true;
      };
    }

    if (!pdfLib || typeof pdfLib.getDocument !== 'function') {
      setStatus('Preparing the reader…');
      setBusy(false);
      setHasPages(false);
      setIsBackgroundRendering(false);
      totalDocumentPagesRef.current = 0;
      pageAspectRatioRef.current = DEFAULT_PAGE_ASPECT_RATIO;
      forceCenterOnNextBoundsUpdateRef.current = true;
      return () => {
        isCancelled = true;
      };
    }

    const hasExistingFlipbook = Boolean(flipInstance.current);
    const keepExistingPagesDuringRefresh = !assetChanged && hasExistingFlipbook;
    const restoreFromPageIndex = keepExistingPagesDuringRefresh
      && typeof flipInstance.current.getCurrentPageIndex === 'function'
      ? flipInstance.current.getCurrentPageIndex()
      : null;

    setIsBackgroundRendering(false);
    setBusy(true);
    if (!keepExistingPagesDuringRefresh) {
      setHasPages(false);
      setStatus('Loading…');
    } else {
      setStatus(null);
    }

    const load = async () => {
      try {
        const pdfUrl = `${BASE_PATH}/pdf/${encodeURIComponent(assetId)}`;
        const documentInstance = await pdfLib.getDocument({ url: pdfUrl, withCredentials: true }).promise;
        if (isCancelled) {
          return;
        }

        const totalPages = documentInstance.numPages;
        totalDocumentPagesRef.current = totalPages;
        if (!totalPages) {
          setStatus('This article is not available for in-app viewing.');
          setPageInfo({ current: 0, total: 0 });
          setHasPages(false);
          setIsBackgroundRendering(false);
          return;
        }

        const dpr = getDevicePixelRatio();
        const zoomScale = appliedZoomPercent / 100;
        const requestedPageIndex = typeof restoreFromPageIndex === 'number' && restoreFromPageIndex >= 0
          ? restoreFromPageIndex
          : 0;
        const clampedPageIndex = Math.max(0, Math.min(requestedPageIndex, totalPages - 1));
        const visibleSpreadStart = Math.floor(clampedPageIndex / 2) * 2;
        const initialRenderIndices = [visibleSpreadStart, visibleSpreadStart + 1]
          .filter((pageIndex, position, collection) => (
            pageIndex >= 0
            && pageIndex < totalPages
            && collection.indexOf(pageIndex) === position
          ));
        const pageImages = new Array(totalPages).fill(null);

        for (const pageIndex of initialRenderIndices) {
          const pageRenderResult = await renderPageToImage(documentInstance, pageIndex, { zoomScale, dpr });
          if (isCancelled) {
            return;
          }
          updatePageAspectRatioFromRender(pageRenderResult.aspectRatio);
          pageImages[pageIndex] = pageRenderResult.dataUrl;
        }

        const placeholder = createPlaceholderPage();
        const buildDisplayPages = () => addTrailingBlank(
          pageImages.map((pageImage) => pageImage || placeholder),
          placeholder,
        );
        const initialPages = buildDisplayPages();
        initFlipbook(initialPages, {
          restoreFromPageIndex: clampedPageIndex,
          forceRecreate: assetChanged || zoomChanged,
        });
        setStatus(null);
        setPageInfo({ current: clampedPageIndex + 1, total: totalPages });
        setBusy(false);

        const remainingIndices = [];
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
          if (!initialRenderIndices.includes(pageIndex)) {
            remainingIndices.push(pageIndex);
          }
        }

        if (!remainingIndices.length) {
          setIsBackgroundRendering(false);
          return;
        }

        setIsBackgroundRendering(true);
        let renderedSinceUpdate = 0;
        for (const pageIndex of remainingIndices) {
          const pageRenderResult = await renderPageToImage(documentInstance, pageIndex, { zoomScale, dpr });
          if (isCancelled) {
            return;
          }
          pageImages[pageIndex] = pageRenderResult.dataUrl;
          renderedSinceUpdate += 1;

          const isLastRemainingPage = pageIndex === remainingIndices[remainingIndices.length - 1];
          if (renderedSinceUpdate < BACKGROUND_UPDATE_BATCH_SIZE && !isLastRemainingPage) {
            continue;
          }
          renderedSinceUpdate = 0;

          const updatedPages = buildDisplayPages();
          const currentPageIndex = flipInstance.current
            && typeof flipInstance.current.getCurrentPageIndex === 'function'
            ? flipInstance.current.getCurrentPageIndex()
            : clampedPageIndex;
          initFlipbook(updatedPages, {
            restoreFromPageIndex: currentPageIndex,
            forceRecreate: false,
          });
        }

        setIsBackgroundRendering(false);
        return;
      } catch (error) {
        console.error('Failed to load PDF preview', error);
        if (isCancelled) {
          return;
        }
        setStatus("We couldn't load this article right now.");
        totalDocumentPagesRef.current = 0;
        setHasPages(false);
        setIsBackgroundRendering(false);
      } finally {
        if (!isCancelled) {
          setBusy(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [
    appliedZoomPercent,
    assetId,
    cleanupFlipbook,
    initFlipbook,
    pdfLib,
    renderPageToImage,
    updateFlipbookBounds,
    updatePageAspectRatioFromRender,
  ]);

  useEffect(() => () => {
    cleanupFlipbook();
  }, [cleanupFlipbook]);

  const flipPrev = () => {
    if (flipInstance.current) {
      flipInstance.current.flipPrev();
    }
  };

  const flipNext = () => {
    if (flipInstance.current) {
      flipInstance.current.flipNext();
    }
  };

  const onZoomChange = (event) => {
    const nextZoom = Number(event.target.value);
    if (Number.isNaN(nextZoom)) {
      return;
    }
    setZoomPercent(nextZoom);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => {
      if (flipInstance.current) {
        flipInstance.current.update();
      }
      updateFlipbookBounds();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateFlipbookBounds]);

  const showStatus = Boolean(status) && !hasPages;

  return (
    <div className={cx('librarian-reader')}>
      <div className={cx('librarian-reader__viewer')} ref={viewerRef}>
        <div className={cx('librarian-reader__flipbook')} ref={containerRef} />
        {showStatus ? <div className={cx('librarian-reader__status')}>{status}</div> : null}
        <div className={cx('librarian-reader__controls')}>
          <div className={cx('librarian-reader__zoom-controls')}>
            <span className={cx('librarian-reader__zoom-label')}>Zoom</span>
            <input
              className={cx('librarian-reader__zoom-slider')}
              type="range"
              min={100}
              max={200}
              step={10}
              value={zoomPercent}
              onChange={onZoomChange}
              disabled={busy && !hasPages}
            />
            <span className={cx('librarian-reader__zoom-value')}>{zoomPercent}%</span>
          </div>
          <div className={cx('librarian-reader__pager-controls')}>
            <button type="button" onClick={flipPrev} disabled={busy || pageInfo.current <= 1}>
              ‹
            </button>
            <div className={cx('librarian-reader__page-info')}>
              {pageInfo.total ? `Page ${pageInfo.current} of ${pageInfo.total}` : 'Preparing pages…'}
            </div>
            <button type="button" onClick={flipNext} disabled={busy || pageInfo.current >= pageInfo.total}>
              ›
            </button>
          </div>
          <div
            className={cx('librarian-reader__updating-hint', {
              'is-visible': hasPages && (isZoomDebouncing || busy || isBackgroundRendering),
            })}
          >
            Updating…
          </div>
        </div>
      </div>
    </div>
  );
};

LibrarianReader.propTypes = {
  assetId: PropTypes.string,
};

LibrarianReader.defaultProps = {
  assetId: null,
};

module.exports = LibrarianReader;
